import type { QuoteDocument } from 'paintscout'
import { uuid } from '../../../util/uuid'
// Clean Quote and subfields of PK, SK, _type,
// (also rmv GSI1PK, GSI1SK, GSI2PK, GSI2SK  on quote)
// and rev and id as _rev and _id for quote
// sets _id & _rev to id and rev for contacts, removes _id & _rev

export function cleanQuote(quote: QuoteDocument | any): QuoteDocument {
  let cleanContact = {} as any
  let cleanSecondaryContact = {} as any
  let cleanStatus = {} as any
  let cleanTerms = {} as any
  let cleanOptions = {} as any
  let cleanSignatures = {} as any
  let cleanPresentation = {} as any
  let cleanAreas = {} as any
  let cleanSubstrates = {} as any
  let cleanLineItems = {} as any

  // Clean Contacts
  if (quote?.contact) {
    cleanContact = {
      ...quote.contact,
      id: quote.contact?.id,
      rev: quote.contact?.rev ?? quote.contact?._rev ?? null
    }
    delete cleanContact?.PK
    delete cleanContact?.SK
    delete cleanContact?._rev
  }
  if (quote?.secondaryContact) {
    cleanSecondaryContact = {
      ...quote.secondaryContact,
      id: quote.secondaryContact?.id,
      rev: quote?.secondaryContact?.rev ?? quote?.secondaryContact?._rev ?? null
    }
    delete cleanSecondaryContact?.PK
    delete cleanSecondaryContact?.SK
    delete cleanSecondaryContact?._rev
  }

  // Clean Status
  if (quote?.status) {
    cleanStatus = {
      ...quote.status
    }
    delete cleanStatus?.PK
    delete cleanStatus?.SK
    delete cleanStatus?._type
  }

  // Clean Terms
  if (quote?.terms) {
    cleanTerms = {
      ...quote.terms
    }
    delete cleanTerms?.PK
    delete cleanTerms?.SK
    delete cleanTerms?._type
  }

  // Clean Options
  if (quote?.options) {
    cleanOptions = {
      ...quote.options
    }
    delete cleanOptions?.PK
    delete cleanOptions?.SK
    delete cleanOptions?._type
  }

  // Clean Signatures
  if (quote?.signatures) {
    cleanSignatures = {
      ...quote.signatures
    }
    delete cleanSignatures?.PK
    delete cleanSignatures?.SK
    delete cleanSignatures?._type
  }

  // Clean Presentation
  if (quote?.presentation) {
    cleanPresentation = {
      ...quote.presentation
    }
    delete cleanPresentation?.PK
    delete cleanPresentation?.SK
    delete cleanPresentation?._type
  }

  // Clean Areas
  if (quote?.areas) {
    const areaKeys = Object.keys(quote.areas)
    cleanAreas = areaKeys.reduce((acc, areaKey) => {
      const area = quote.areas[areaKey]
      acc[areaKey] = {
        ...area
      }
      delete acc[areaKey].PK
      delete acc[areaKey].SK
      delete acc[areaKey]._type

      return acc
    }, {})
  }

  // Clean Substrates
  if (quote?.substrates) {
    const substrateKeys = Object.keys(quote.substrates)
    cleanSubstrates = substrateKeys.reduce((acc, substrateKey) => {
      const substrate = quote.substrates[substrateKey]
      acc[substrateKey] = {
        ...substrate
      }
      delete acc[substrateKey].PK
      delete acc[substrateKey].SK
      delete acc[substrateKey]._type

      return acc
    }, {})
  }

  // Clean Line Items
  if (quote?.lineItems) {
    const lineItemKeys = Object.keys(quote.lineItems)
    cleanLineItems = lineItemKeys.reduce((acc, lineItemKey) => {
      const lineItem = quote.lineItems[lineItemKey]
      acc[lineItemKey] = {
        ...lineItem
      }
      delete acc[lineItemKey].PK
      delete acc[lineItemKey].SK
      delete acc[lineItemKey]._type

      return acc
    }, {})
  }

  // Assemble and clean quote
  const cleanQuote: QuoteDocument = {
    ...quote,
    _id: uuid(),
    contact: { ...cleanContact },
    secondaryContact: { ...cleanSecondaryContact },
    status: { ...cleanStatus },
    terms: { ...cleanTerms },
    options: { ...cleanOptions },
    signatures: { ...cleanSignatures },
    presentation: { ...cleanPresentation },
    areas: { ...cleanAreas },
    substrates: { ...cleanSubstrates },
    lineItems: { ...cleanLineItems }
  }

  // Strip legacy DynamoDB keys and _rev that may exist on older documents at runtime
  const {
    PK: _PK,
    SK: _SK,
    GSI1PK: _GSI1PK,
    GSI1SK: _GSI1SK,
    GSI2PK: _GSI2PK,
    GSI2SK: _GSI2SK,
    _rev,
    ...stripped
  } = cleanQuote as QuoteDocument & Record<string, unknown>

  return stripped as QuoteDocument
}
